<template>
    <Form ref="elFormObj" as="" v-slot="{ errors: formErrors, meta: formMeta }">
        <template v-if="partials.length === 1">
            <Field
                v-model="partials[0]!.ethnicity"
                name="ethnicity"
                label="Ethnicity"
                v-slot="{ field, errors }"
                rules="required"
            >
                <v-select
                    v-bind="field"
                    :model-value="field.value"
                    @update:model-value="setPartial($event, 0)"
                    :error-messages="errors"
                    :items="ethnicityList"
                    item-title="text"
                    item-value="ethnicity"
                    name="ethnicity"
                    variant="outlined"
                    hide-details
                    required
                ></v-select>
            </Field>
        </template>
        <template v-if="partials.length > 1">
            <v-row v-for="(partial, index) in partials" :key="index" density="compact">
                <v-col>
                    <Field
                        v-model="partials[index]!.ethnicity"
                        @update:model-value="setPartial($event, index)"
                        :name="`ethnicity_${index}`"
                        label="Ethnicity"
                        v-slot="{ field, errors }"
                        rules="required"
                    >
                        <v-select
                            v-bind="field"
                            :model-value="field.value"
                            :error-messages="errors"
                            :items="ethnicityList"
                            item-title="text"
                            item-value="ethnicity"
                            item-disabled="disabled"
                            name="ethnicity"
                            variant="outlined"
                            hide-details
                            required
                        ></v-select>
                    </Field>
                </v-col>
                <v-col cols="auto" class="d-flex align-center">
                    <v-btn-toggle
                        v-model.number="partial.split"
                        class="ethnicity-partial"
                        @update:model-value="onPartialSplitChange()"
                        mandatory
                    >
                        <v-btn :value="0.25" aria-label="Quarter">25%</v-btn>
                        <v-btn :value="0.5" aria-label="Half" :disabled="partials.length > 3">50%</v-btn>
                        <v-btn :value="0.75" aria-label="Three Quarters" :disabled="partials.length > 2">75%</v-btn>
                    </v-btn-toggle>
                </v-col>
                <v-col cols="1" class="d-flex align-center">
                    <v-btn
                        @click="removePartial(partial.ethnicity, index)"
                        :icon="mdiMinusCircle"
                        variant="text"
                    >
                    </v-btn>
                </v-col>
            </v-row>
            <div v-if="partialsTotal !== 1" class="text-error v-messages px-3 py-1">
                Your selections must total 100%
            </div>
        </template>

        <v-btn
            @click="addPartial()"
            variant="flat"
            class="mt-2"
            color="secondary"
            type="button"
            :disabled="!lo_isEmpty(formErrors) || partials[0]!.ethnicity === 'None' || partials.length >= 4"
            size="small"
        >
            <v-icon :icon="mdiPlusThick" class="mr-1"></v-icon>
            Add Partial Ethnicity
        </v-btn>
    </Form>
</template>

<script lang="ts" setup>
    import type { Field, Form } from 'vee-validate';
    import { EthnicityList } from '~/models';
    import type { EthnicityListItem, EthnicityListItemValue, EthnicityInputValue } from '~/types';
    import { EthnicityInputValueKeys } from '~/types';
    import { mdiMinusCircle, mdiPlusThick } from '@mdi/js';
    import { initVeeRules } from '~/helpers/forms';
    import { get as lo_get } from 'es-toolkit/compat';
    import { set as lo_set } from 'es-toolkit/compat';
    import { isNil as lo_isNil } from 'es-toolkit';
    import { round as lo_round } from 'es-toolkit';
    import { isEmpty as lo_isEmpty } from 'es-toolkit/compat';
    
    
    initVeeRules([ 'required' ]);

    const props = defineProps({
        modelValue: {
            type: Object as PropType<Nilable<EthnicityInputValue>>,
            required: false,
            default: null
        },
        label: {
            type: String as PropType<string>,
            required: true
        }
    });

    const emit = defineEmits([
        'update:modelValue'
    ]);

    const elFormObj = shallowRef<typeof Form>();
    const ethnicityList = getEthnicityList();
    const partials = reactive<EthnicityListItemValue[]>(loadInitial());
    const partialsTotal = ref(1);

    function getListItem(
        ethnicity: Nullable<EthnicityInputValueKeys>,
        split: number,
        disabled: boolean
    ): EthnicityListItemValue {
        return {
            ethnicity,
            split,
            props: { disabled }
        };
    }

    function getEthnicityList(): EthnicityListItem[] {
        return EthnicityList.map((o) => {
            o.props = { disabled: false };
            return o;
        });
    }

    function loadInitial(): EthnicityListItemValue[] {
        const initialPartials: EthnicityListItemValue[] = [getListItem(null, 1, false)];

        if (props.modelValue) {
            try {
                const valueObj: { [key: string]: number } = props.modelValue;
                const keys: EthnicityInputValueKeys[] = Object.keys(valueObj) as EthnicityInputValueKeys[];

                initialPartials.pop();

                keys.forEach((key: EthnicityInputValueKeys) => {
                    initialPartials.push(
                        getListItem(key, valueObj[key]!, false)
                    );
                });
            } catch (error) {}
        }

        return initialPartials;
    }

    function toggleDisabled(ethnicity: EthnicityInputValueKeys, state: boolean): void {
        partials.forEach((o: EthnicityListItemValue) => {
            if (!ethnicity) {
                return;
            }

            const idx = ethnicityList.findIndex((o) => o.ethnicity === ethnicity);

            if (idx > -1) {
                ethnicityList[idx]!.props!.disabled = state;
            }
        });
    }

    function addPartial(): void {
        let split;

        if (partials.length === 1) {
            split = partials[0]!.split = 0.5;
        } else {
            split = lo_round(1 / (partials.length + 1), 2);
        }

        partials.push({
            ethnicity: null,
            split
        });
    }

    async function updateValue(): Promise<void> {
        const validateObj = await elFormObj.value?.validate();

        if (validateObj.valid) {
            let splitTotal = 0;
            const reduced: Partial<EthnicityInputValue> = partials.reduce(
                (r: Partial<EthnicityInputValue>, o: EthnicityListItemValue) => {
                    if (!lo_isNil(o.ethnicity)) {
                        r[o.ethnicity] = o.split;

                        splitTotal += o.split;
                    }

                    return r;
                },
                {}
            );

            partialsTotal.value = splitTotal;

            if (splitTotal !== 1) {
                emit('update:modelValue', null);
                return;
            }

            emit('update:modelValue', reduced);
        }
    }

    function setPartial(ethnicity: EthnicityInputValueKeys, idx: number) {
        const prevEthnicity = lo_get(partials, [idx, 'ethnicity']);

        // Make sure to un-disable a changed value
        if (prevEthnicity) {
            toggleDisabled(prevEthnicity, false);
        }

        lo_set(partials, [idx, 'ethnicity'], ethnicity);
        toggleDisabled(ethnicity, true);
        updateValue();
    }

    function removePartial(ethnicity: EthnicityInputValueKeys | null, idx: number) {
        
        // Remove the partial
        partials.splice(idx, 1);

        if (ethnicity) {
            toggleDisabled(ethnicity, false);

            // Reset the split to 1 if only 1 ethnicity
            if (partials.length === 1) {
                partials[0]!.split = 1;
            }

            updateValue();
        }
    }

    function onPartialSplitChange() {
        updateValue();
    }
</script>

<style lang="scss" scoped></style>
